/**
 * Minimal block parser for the global broadcast note.
 *
 * The note is authored by a program lead in a plain textarea, not by an
 * engineer, so this deliberately supports only what someone would type by
 * instinct: blank lines to separate paragraphs, and a leading dash or bullet
 * to make a list. There is no inline syntax (no bold, no links) and no
 * Markdown dependency.
 *
 * Nothing here produces HTML. The caller renders the returned blocks as React
 * elements, so a note can never inject markup. That matters because
 * app_config is writable by every admin, and one of those admins is a shared
 * team alias.
 */

export type BroadcastBlock =
  | { kind: "paragraph"; text: string } // may contain newlines; render pre-line
  | { kind: "list"; items: string[] };

/** A line like "- thing", "* thing", or "• thing". */
const BULLET = /^\s*[-*•]\s+(.*)$/;

export function parseBroadcastNote(note: string): BroadcastBlock[] {
  const blocks: BroadcastBlock[] = [];
  let paragraph: string[] = [];
  let list: string[] = [];

  const flushParagraph = () => {
    const text = paragraph.join("\n").trim();
    if (text) blocks.push({ kind: "paragraph", text });
    paragraph = [];
  };
  const flushList = () => {
    if (list.length) blocks.push({ kind: "list", items: list });
    list = [];
  };

  for (const rawLine of note.replace(/\r\n?/g, "\n").split("\n")) {
    const bullet = rawLine.match(BULLET);

    if (bullet) {
      // A list interrupts a paragraph, and vice versa.
      flushParagraph();
      const item = bullet[1].trim();
      if (item) list.push(item);
      continue;
    }

    flushList();

    if (!rawLine.trim()) {
      // Blank line ends the current paragraph rather than adding to it, so
      // consecutive blanks collapse instead of producing empty blocks.
      flushParagraph();
      continue;
    }

    paragraph.push(rawLine.trim());
  }

  flushList();
  flushParagraph();
  return blocks;
}
